import React, { useCallback } from "react";
import {
    useLocation,
    useNavigate,
} from "react-router-dom";
import { api } from "@/lib/api";
import {
    ArrowLeft,
    Check,
    Crown,
    Sparkles,
} from "lucide-react";

const Plans = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const selectedPlan =
        location.state?.selectedPlan || null;

    const plans = [
        {
            id: "monthly",
            name: "Monthly",
            price: "$15",
            suffix: "/month",
            trialText: "7-day free trial",
            description:
                "Flexible month-to-month access to Liberi Curiosi.",
            features: [
                "Parent portal",
                "Child classrooms",
                "Calendars and scheduling",
                "Quests and assignments",
                "Notebooks and whiteboards",
                "Progress and academic records",
                "Family and classroom tools",
            ],
        },
        {
            id: "annual",
            name: "Annual",
            price: "$150",
            suffix: "/year",
            trialText: "7-day free trial",
            description:
                "A full year of Liberi Curiosi with a lower effective monthly cost.",
            features: [
                "Everything in Monthly",
                "One annual renewal",
                "Lower yearly cost",
                "Continuous access across the school year",
                "Same family account and saved school data",
            ],
            featured: true,
        },
    ];

    const handleChoosePlan = useCallback(async (planId) => {
        try {
            const me = await api.get("/auth/me");

            if (!me?.data) {
                navigate("/parent/login");
                return;
            }

            const response = await api.post(
                "/billing/checkout",
                {
                    interval: planId,
                }
            );

            const checkoutUrl =
                response.data?.checkout_url;

            if (!checkoutUrl) {
                window.alert(
                    "Unable to open Stripe Checkout."
                );
                return;
            }

            window.location.href = checkoutUrl;
        } catch (error) {
            const status =
                error?.response?.status;

            if (status === 401) {
                navigate(
                    "/parent/login",
                    {
                        state: {
                            returnTo: "/plans",
                            selectedPlan: planId,
                        },
                    }
                );

                return;
            }

            console.error(
                "Unable to start plan checkout:",
                error
            );

            window.alert(
                error?.response?.data?.detail ||
                    "Unable to start checkout."
            );
        }
        }, [navigate]);
    
    React.useEffect(() => {
        if (!selectedPlan) return;

        handleChoosePlan(selectedPlan);

        navigate(
            "/plans",
            {
                replace: true,
                state: {},
            }
        );
    }, [selectedPlan, handleChoosePlan, navigate]);
    return (
        <div className="min-h-screen bg-[#05070d] text-white">
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(124,58,237,0.16),transparent_36%)]" />

                <div className="relative z-10 mx-auto max-w-6xl px-6 py-10">
                    <button
                        type="button"
                        onClick={() =>
                            navigate("/")
                        }
                        className="
                            inline-flex
                            items-center
                            gap-2
                            rounded-2xl
                            border
                            border-[#ffd166]/35
                            bg-black/70
                            px-4
                            py-2
                            text-sm
                            font-semibold
                            text-[#fff3c4]
                            transition
                            hover:border-[#ffd166]
                            hover:text-[#ffd166]
                        "
                    >
                        <ArrowLeft size={16} />
                        Back to Home
                    </button>

                    <section className="mx-auto mt-14 max-w-3xl text-center">
                        <div
                            className="
                                mx-auto
                                flex
                                h-16
                                w-16
                                items-center
                                justify-center
                                rounded-full
                                border
                                border-[#ffd166]/55
                                bg-[#2b1548]/70
                                text-[#ffd166]
                                shadow-[0_0_24px_rgba(168,85,247,0.35)]
                            "
                        >
                            <Sparkles size={28} />
                        </div>

                        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.28em] text-[#d8b4fe]">
                            Liberi Curiosi
                        </p>

                        <h1 className="mt-3 font-display text-5xl text-[#ffd166]">
                            Choose Your Academy Plan
                        </h1>

                        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#b0b8d6]">
                            Your family account and school
                            records remain attached to the
                            same Liberi profile. If you pause
                            your subscription, your profile is
                            preserved during the retention
                            period and can be restored when
                            you return.
                        </p>
                    </section>

                    <section className="mt-14 grid gap-6 md:grid-cols-2">
                        {plans.map((plan) => (
                            <article
                                key={plan.id}
                                className={`
                                    relative
                                    rounded-[32px]
                                    border
                                    p-7
                                    border-[#c084fc]/30 bg-black/75
                                `}
                            >
                                {plan.featured && (
                                    <div
                                        className="
                                            absolute
                                            right-5
                                            top-5
                                            inline-flex
                                            items-center
                                            gap-2
                                            rounded-full
                                            border
                                            border-[#ffd166]/40
                                            bg-[#2b1548]/80
                                            px-3
                                            py-1
                                            text-xs
                                            font-semibold
                                            text-[#ffd166]
                                        "
                                    >
                                        <Crown size={14} />
                                        Best Value
                                    </div>
                                )}

                                <h2 className="font-display text-3xl text-[#ffd166]">
                                    {plan.name}
                                </h2>

                                <div className="mt-5 flex items-end gap-2">
                                    <span className="font-display text-5xl text-[#fff3c4]">
                                        {plan.price}
                                    </span>

                                    <span className="pb-1 text-sm text-[#9ca3b7]">
                                        {plan.suffix}
                                    </span>
                                </div>
                                
                                <p className="mt-2 text-sm font-semibold text-[#7dffb2]">
                                    {plan.trialText}
                                </p>
                                <p className="mt-4 text-sm leading-relaxed text-[#b0b8d6]">
                                    {plan.description}
                                </p>

                                <div className="mt-6 space-y-3">
                                    {plan.features.map(
                                        (feature) => (
                                            <div
                                                key={feature}
                                                className="flex items-start gap-3 text-sm text-[#d7dbea]"
                                            >
                                                <Check
                                                    size={17}
                                                    className="mt-0.5 shrink-0 text-[#ffd166]"
                                                />

                                                <span>
                                                    {feature}
                                                </span>
                                            </div>
                                        )
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        handleChoosePlan(
                                            plan.id
                                        )
                                    }
                                    className={`
                                        mt-8
                                        w-full
                                        rounded-2xl
                                        border
                                        px-5
                                        py-4
                                        font-semibold
                                        transition
                                        ${
                                            plan.featured
                                                ? "border-[#ffd166]/60 bg-[#2b1548]/80 text-[#fff3c4] hover:border-[#ffd166] hover:text-[#ffd166]"
                                                : "border-[#c084fc]/40 bg-[#160d26]/55 text-[#e9d5ff] hover:border-[#c084fc]"
                                        }
                                    `}
                                >
                                    Choose {plan.name}
                                </button>
                            </article>
                        ))}
                    </section>

                    <section
                        className="
                            mt-10
                            rounded-[28px]
                            border
                            border-[#ffd166]/20
                            bg-black/65
                            p-6
                            text-center
                        "
                    >
                        <h3 className="font-display text-2xl text-[#ffd166]">
                            Already have access?
                        </h3>

                        <p className="mt-2 text-sm text-[#b0b8d6]">
                            Testers and active subscribers can
                            return directly to the parent
                            portal.
                        </p>

                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    "/parent/login"
                                )
                            }
                            className="
                                mt-5
                                rounded-2xl
                                border
                                border-[#ffd166]/35
                                bg-[#2b1548]/60
                                px-5
                                py-3
                                font-semibold
                                text-[#fff3c4]
                                transition
                                hover:border-[#ffd166]
                                hover:text-[#ffd166]
                            "
                        >
                            Parent Sign In
                        </button>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Plans;